import { useAppSelector } from 'src/hooks/useStore';

/**
 * Tooltip copy shown when bank/ACH options are disabled because the portal's
 * connected Stripe account can't accept ACH yet. It points the internal user
 * to their Stripe dashboard, where the `us_bank_account_ach_payments`
 * capability (ACH Direct Debit) is enabled/completed.
 */
export const ACH_CAPABILITY_DISABLED_TOOLTIP =
  "Bank (ACH) payments aren't enabled for your account yet. Finish setting up ACH Direct Debit in your Stripe dashboard to accept bank payments.";

export type ACHCapabilityState = {
  /** True when the connected account has us_bank_account_ach_payments active. */
  hasACHCapability: boolean;
  /**
   * Whether we actually know the account's capability state. It is only known
   * when the connected account has been loaded into `accountInfo` (internal
   * user surfaces). In contexts without it, callers should preserve existing
   * behavior rather than gray out bank options.
   */
  isACHCapabilityResolved: boolean;
  /** Convenience: capability is known AND not active — safe to gray out bank UI. */
  shouldBlockBankPayments: boolean;
};

/**
 * Reports whether the portal's connected Stripe account can accept ACH bank
 * payments, derived from the Stripe Account `capabilities` map already loaded
 * into Redux via `SetPaymentInfo`. Charges made on behalf of the connected
 * account fail when this capability isn't active, so bank options must be
 * gated on it.
 */
export const useHasACHCapability = (): ACHCapabilityState => {
  const accountInfo = useAppSelector((state) => state.payments.accountInfo);

  const isACHCapabilityResolved = Boolean(accountInfo);
  const hasACHCapability =
    accountInfo?.capabilities?.us_bank_account_ach_payments === 'active';

  return {
    hasACHCapability,
    isACHCapabilityResolved,
    shouldBlockBankPayments: isACHCapabilityResolved && !hasACHCapability,
  };
};
